/// <reference path="../../../node_modules/react-datetime/react-datetime.d.ts" />

import * as React from 'react';
import { Translate } from '../../../../shared/LanguageService';
import {
    FormGroup,
    FormControl,
    ControlLabel,
    HelpBlock,
    Checkbox,
    Row,
    Col
} from 'react-bootstrap';
import { ICreateOrderOther, INamedIdentity } from '../../Entities';
import DateTime from 'react-datetime';
import FormSelect from '../../../../components/common/FormSelect';
import ValidatableFormGroup from '../../../../components/common/ValidatableFormGroup';
import { Form } from 'react-bootstrap/lib/Navbar';

interface ICreateOrderOtherFormProps {
    other: ICreateOrderOther;
    onDataChange: (e: any) => void;
    isFormValid: () => boolean;
    errors: any;
    isPackageOrder: boolean;
    isStylingOrder: boolean;
    isCommercial: boolean;
    onPreferredTimeOfPhotographingDateChange: (e: any) => void;
    availableContactPersons: INamedIdentity[];
    hasKeyProductSelected?: boolean;
    selectedBrokerFirm?: any;
}

const CreateOrderOtherForm = (props: ICreateOrderOtherFormProps) => {
    const useBroker = Boolean((props.other as any).useBrokerFirmAddress);

    const setOtherText = (name: string, value: string) => {
        props.onDataChange({ target: { type: 'text', name, value } });
    };

    const setOtherCheckbox = (name: string, checked: boolean) => {
        props.onDataChange({ target: { type: 'checkbox', name, checked } });
    };

    const getBrokerFirmAddress = (firm: any) => {
        if (!firm) return '';
        return (firm.visitorAddress || '').trim();
    };

    const getBrokerFirmZip = (firm: any) => {
        if (!firm) return '';
        return (firm.visitorZip || '').trim();
    };

    const getBrokerFirmCity = (firm: any) => {
        if (!firm) return '';
        return (firm.visitorCity || '').trim();
    };

    React.useEffect(() => {
        if (!props.hasKeyProductSelected) return;

        const current = Boolean((props.other as any).useBrokerFirmAddress);
        if (!current) {
            setOtherCheckbox('other.useBrokerFirmAddress', true);
        }
    }, [props.hasKeyProductSelected]);

    React.useEffect(() => {
        if (!useBroker) return;
        if (!props.selectedBrokerFirm) return;

        const addr = getBrokerFirmAddress(props.selectedBrokerFirm);
        const zip = getBrokerFirmZip(props.selectedBrokerFirm);
        const city = getBrokerFirmCity(props.selectedBrokerFirm);
        const currentAddr = (props.other as any).keyPickupAddress || '';
        const currentZip = (props.other as any).keyPickupZip || '';
        const currentCity = (props.other as any).keyPickupCity || '';

        if (addr && addr !== currentAddr) {
            setOtherText('other.keyPickupAddress', addr);
        }

        if (zip && zip !== currentZip) {
            setOtherText('other.keyPickupZip', zip);
        }

        if (city && city !== currentCity) {
            setOtherText('other.keyPickupCity', city);
        }
    }, [useBroker, props.selectedBrokerFirm]);

    return (
        <fieldset>
            <legend>{Translate('orders/create/form/header/other')}</legend>
            <form>
                {props.isPackageOrder && (
                    <FormGroup>
                        <Checkbox
                            className="inline"
                            name="other.willOrderCreatorAttendWhenPhotographing"
                            checked={props.other.willOrderCreatorAttendWhenPhotographing}
                            onChange={props.onDataChange}
                        >
                            <span>
                                {!props.isCommercial &&
                                    !props.isStylingOrder &&
                                    Translate(
                                        'orders/create/form/input-label/will-contact-attend-when-photographing-private'
                                    )}
                                {props.isCommercial &&
                                    !props.isStylingOrder &&
                                    Translate(
                                        'orders/create/form/input-label/will-contact-attend-when-photographing-commercial'
                                    )}
                                {!props.isCommercial &&
                                    props.isStylingOrder &&
                                    Translate(
                                        'orders/create/form/input-label/will-contact-attend-when-styling-private'
                                    )}
                                {props.isCommercial &&
                                    props.isStylingOrder &&
                                    Translate(
                                        'orders/create/form/input-label/will-contact-attend-when-styling-commercial'
                                    )}
                            </span>
                        </Checkbox>
                        {!props.other.willOrderCreatorAttendWhenPhotographing && (
                            <>
                                <HelpBlock>
                                    {!props.isStylingOrder &&
                                        Translate(
                                            'orders/create/form/input-helptext/will-contact-attend-when-photographing'
                                        )}
                                    {props.isStylingOrder &&
                                        Translate(
                                            'orders/create/form/input-helptext/will-contact-attend-when-styling'
                                        )}
                                </HelpBlock>
                                <HelpBlock>
                                    {Translate(
                                        'orders/create/form/input-helptext/extra-information-regarding-key-pickup'
                                    )}
                                </HelpBlock>
                            </>
                        )}
                    </FormGroup>
                )}
                {props.hasKeyProductSelected && (
                    <div>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('orders/create/form/input-label/key-pickup-address')}
                                <i className="material-icons" style={{ marginLeft: '4px' }}>
                                    vpn_key
                                </i>
                            </ControlLabel>
                        </FormGroup>

                        <FormGroup>
                            <div style={{ marginBottom: '6px' }}>
                                <Checkbox
                                    className="inline"
                                    name="other.useBrokerFirmAddress"
                                    checked={useBroker}
                                    disabled={!props.selectedBrokerFirm}
                                    onChange={(e: any) => {
                                        const checked = Boolean(e.target.checked);
                                        setOtherCheckbox('other.useBrokerFirmAddress', checked);

                                        if (checked && props.selectedBrokerFirm) {
                                            const addr = getBrokerFirmAddress(
                                                props.selectedBrokerFirm
                                            );
                                            const zip = getBrokerFirmZip(props.selectedBrokerFirm);
                                            const city = getBrokerFirmCity(
                                                props.selectedBrokerFirm
                                            );

                                            setOtherText('other.keyPickupAddress', addr);
                                            setOtherText('other.keyPickupZip', zip);
                                            setOtherText('other.keyPickupCity', city);
                                        }
                                    }}
                                >
                                    <span>
                                        {Translate(
                                            'orders/create/form/input-label/use-broker-firm-address'
                                        )}
                                    </span>
                                </Checkbox>
                            </div>
                        </FormGroup>

                        <ValidatableFormGroup
                            required
                            validationKey="other.keyPickupAddress"
                            errors={props.errors}
                        >
                            <ControlLabel>
                                {Translate('orders/create/form/input-label/key-pickup-street')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="other.keyPickupAddress"
                                value={(props.other as any).keyPickupAddress || ''}
                                onChange={props.onDataChange}
                                disabled={useBroker}
                            />
                        </ValidatableFormGroup>

                        <ValidatableFormGroup
                            required
                            validationKey="other.keyPickupZip"
                            errors={props.errors}
                        >
                            <ControlLabel>
                                {Translate('orders/create/form/input-label/key-pickup-zip')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="other.keyPickupZip"
                                value={(props.other as any).keyPickupZip || ''}
                                onChange={props.onDataChange}
                                disabled={useBroker}
                            />
                        </ValidatableFormGroup>

                        <ValidatableFormGroup
                            required
                            validationKey="other.keyPickupCity"
                            errors={props.errors}
                        >
                            <ControlLabel>
                                {Translate('orders/create/form/input-label/key-pickup-city')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="other.keyPickupCity"
                                value={(props.other as any).keyPickupCity || ''}
                                onChange={props.onDataChange}
                                disabled={useBroker}
                            />
                        </ValidatableFormGroup>
                    </div>
                )}
                {props.isPackageOrder && (
                    <ValidatableFormGroup validationKey="other.Notes" errors={props.errors}>
                        <ControlLabel>
                            {Translate('orders/create/form/input-label/notes')}
                        </ControlLabel>
                        <FormControl
                            componentClass="textarea"
                            name="other.notes"
                            value={props.other.notes ? props.other.notes : ''}
                            onChange={props.onDataChange}
                        />
                    </ValidatableFormGroup>
                )}
                {!props.isStylingOrder && (
                    <ValidatableFormGroup
                        validationKey="other.ExtraDeliveryRecipients"
                        errors={props.errors}
                    >
                        <ControlLabel>
                            {Translate('orders/create/form/input-label/extra-delivery-recipients')}
                        </ControlLabel>
                        <FormControl
                            style={{ marginBottom: '7px' }}
                            type="text"
                            name="other.extraDeliveryRecipients"
                            value={
                                props.other.extraDeliveryRecipients
                                    ? props.other.extraDeliveryRecipients
                                    : ''
                            }
                            onChange={props.onDataChange}
                        />
                        <HelpBlock>
                            {Translate(
                                'orders/create/form/input-helptext/extra-delivery-recipients'
                            )}
                        </HelpBlock>
                    </ValidatableFormGroup>
                )}
            </form>
        </fieldset>
    );
};

export default CreateOrderOtherForm;
